import { climateEncounterField, climateItemId, encounterClimateShape, climateGroundAt, prepareClimateGround, ICE_SURFACE_ID, SNOW_GROUND_ID, type ClimateGroundContext, type GroundCondition } from './climate.js';
import { collectPersistentZones, zoneContains, zoneShape, zoneIsPainted, ZoneHandBack, type Zone } from '../zones/zone-membership.js';
import { refreshClassAuraZones, type WalkZoneReaders } from '../zones/class-aura-refresh.js';
import { combatSizeFt } from '../../shared/combat-size.js';
import { posCoord, inventoryField, encounterCreatureField, itemsCol } from '../../shared/array-types.js';
import { parseSurfaceGeometry } from '../../shared/surface-geometry.js';
type Row = Record<string, unknown>;

export interface DifficultTerrainClimateContext {
  encounter: Row; shape: unknown; weather: Row | null;
  ignoresTerrain: boolean; ignoresSnowAndIce: boolean; walksOnWater: boolean;
  ground?: ClimateGroundContext;
  /** Prepared once per walk; held anchors retain their native 3D shape and live carrier position. */
  zones?: readonly DifficultTerrainZone[];
  footprints?: readonly unknown[];
  movingZoneHolderId?: string;
  swimSpeedFt?: number;
  wadeThresholdFt?: number;
  squeezeSizeCategoryOffset?: number;
}
export function groundConditionIsDifficult(condition: GroundCondition, flags: Pick<DifficultTerrainClimateContext, 'ignoresTerrain' | 'ignoresSnowAndIce' | 'walksOnWater'>): boolean {
  return condition !== 'none' && !flags.ignoresTerrain && !flags.ignoresSnowAndIce && !(condition === 'ice' && flags.walksOnWater);
}
export function movementClimateContext(encounter: Row, mover: Row, read: DifficultTerrainReaders, shape: unknown = encounterClimateShape(encounter)): DifficultTerrainClimateContext {
  const weatherId = climateEncounterField(encounter, 'encounter_weather');
  const weather = read.definition(climateItemId(weatherId)) ?? record(record(encounter.catalog)?.[climateItemId(weatherId)]) ?? record(weatherId);
  return { encounter, shape, weather, ground: prepareClimateGround(encounter, shape, weather),
    zones: encounterDifficultZones(encounter, read),
    footprints: encounterDifficultFootprints(encounter),
    movingZoneHolderId: String(encounterCreatureField(mover.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase(),
    squeezeSizeCategoryOffset: moverMovementInt(mover, read, 'squeeze_size_category_offset'),
    swimSpeedFt: Number(read.value(mover, 'speed_swimming') ?? 0),
    ignoresTerrain: moverHasMovementFlag(mover, read, 'ignores_difficult_terrain'),
    ignoresSnowAndIce: moverHasMovementFlag(mover, read, 'ignores_snow_and_ice_terrain'),
    walksOnWater: moverHasMovementFlag(mover, read, 'walks_on_water_and_ice') };
}
export function movementClimateGround(climate: DifficultTerrainClimateContext): ClimateGroundContext {
  const ground = climate.ground;
  if (ground && ground.encounter === climate.encounter && ground.shape === climate.shape && ground.weather === climate.weather) return ground;
  return climate.ground = prepareClimateGround(climate.encounter, climate.shape, climate.weather);
}

export interface NavigationFootprint extends Row {
  schema: 'ft.ft3d-navigation-footprint.v1';
  source: 'legacy-size-box';
  bottom: number;
  top: number;
  polygons: number[][][];
  inventory_id?: string;
}

export interface DifficultTerrainReaders {
  value: (row: Row, name: string) => unknown;
  entries: (row: Row, name: string) => unknown[];
  definition: (id: string) => Row | null;
  statuses: (row: Row) => Row[];
  position?: (row: Row) => readonly [number, number, number];
}

export const DIFFICULT_TERRAIN_TYPE_ID = '60160881-7F8B-A3ED-4D1A-D80C8397E634';
export interface DifficultTerrainZone { zone: Zone; ground: GroundCondition }

/** Read current held definitions, rather than the compile-time zone-key projection (new spills are row deltas). */
export function encounterDifficultZones(encounter: Row, read: DifficultTerrainReaders): DifficultTerrainZone[] {
  const zoneRead: WalkZoneReaders = {
    position: row => {
      const at = read.position?.(row);
      return at ? [at[0], at[1], at[2]] : [posCoord(row, 'x_pos') ?? 0, posCoord(row, 'y_pos') ?? 0, posCoord(row, 'z_pos') ?? 0];
    },
    occupancy: () => null, rayZ: () => 0,
    definition: read.definition, has: (row, key) => read.value(row, key) != null, layer: read.value,
    combatSizeFt: row => combatSizeFt(row, { value: (value, key) => value ? read.value(value, key) : null, inventoryField }),
  };
  const zones = collectPersistentZones(encounter, zoneRead);
  const difficult: DifficultTerrainZone[] = [];
  for (const zone of zones) {
    const s = zone.status;
    const definition = read.definition(String(s.status_origin_id || s.status_item_id || '').toUpperCase());
    if (!definition) continue; // The existing zone sweep names missing definitions before admitting the walk.
    const terrain = ['terrain_created', 'terrain_type'].map(key => climateItemId(read.value(definition, key)));
    const ownId = climateItemId(itemsCol(definition, 'item_id'));
    if (!terrain.includes(DIFFICULT_TERRAIN_TYPE_ID) && ownId !== DIFFICULT_TERRAIN_TYPE_ID) continue;
    // Unsupported legacy geometry is judged by the walk's spatially scoped zone gate. It must not refuse unrelated feet here.
    try { if (!parseSurfaceGeometry(s.status_aoe_geometry)?.footprint && !zoneIsPainted(zone)) zoneShape(zone); }
    catch (error) { if (error instanceof ZoneHandBack) continue; throw error; }
    // Surface identity covers Ice Patch, whose authored difficult terrain has no ground_terrain_type layer.
    // Retain the ground tag for older snow/ice zones without surface_type.
    const ground = climateItemId(read.value(definition, 'ground_terrain_type'));
    const surface = climateItemId(read.value(definition, 'surface_type'));
    difficult.push({ zone, ground: ground === ICE_SURFACE_ID || surface === 'ICE' ? 'ice' : ground === SNOW_GROUND_ID ? 'snow' : 'none' });
  }
  // Entry/exit and pricing judge class auras at the same current radius, without changing their held anchor rows.
  const creatures = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
  const refreshed = refreshClassAuraZones(difficult.map(entry => entry.zone), creatures, zoneRead, { lenient: true });
  return difficult.map((entry, i) => refreshed[i] === entry.zone ? entry : { ...entry, zone: refreshed[i]! });
}

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const bucket = (row: Row, name: string): Row => record(row[name]) ?? {};
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const nonEmpty = (value: unknown): boolean => {
  value = scalar(value);
  if (value === undefined || value === null || value === false || value === 0 || value === '' || value === '0') return false;
  return !Array.isArray(value) || value.length > 0;
};

/** Warm-cache compatibility twin of ft3d_navigation_footprints_from_boxes(). */
export function footprintsFromBoxes(boxes: readonly unknown[]): NavigationFootprint[] {
  const out: NavigationFootprint[] = [];
  for (const value of boxes) {
    const box = record(value);
    if (!box) continue;
    const footprint: NavigationFootprint = {
      schema: 'ft.ft3d-navigation-footprint.v1', source: 'legacy-size-box',
      bottom: Number(box.bottom ?? 0), top: Number(box.top ?? 0),
      polygons: [[
        [Number(box.left ?? 0), Number(box.near ?? 0)],
        [Number(box.right ?? 0), Number(box.near ?? 0)],
        [Number(box.right ?? 0), Number(box.far ?? 0)],
        [Number(box.left ?? 0), Number(box.far ?? 0)],
      ]],
    };
    if (typeof box.inventory_id === 'string') footprint.inventory_id = box.inventory_id;
    out.push(footprint);
  }
  return out;
}

function pointInPolygon(x: number, y: number, polygon: unknown[]): boolean {
  let inside = false;
  if (polygon.length < 3) return false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = Array.isArray(polygon[j]) ? polygon[j] as unknown[] : [];
    const b = Array.isArray(polygon[i]) ? polygon[i] as unknown[] : [];
    const ax = Number(a[0] ?? 0), ay = Number(a[1] ?? 0);
    const bx = Number(b[0] ?? 0), by = Number(b[1] ?? 0);
    const dx = bx - ax, dy = by - ay, lengthSq = dx * dx + dy * dy;
    const t = lengthSq > 1e-12 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / lengthSq)) : 0;
    if (Math.hypot(x - (ax + dx * t), y - (ay + dy * t)) <= 1e-6) return true;
    if ((ay > y) !== (by > y) && x < (bx - ax) * (y - ay) / ((by - ay) || 1e-12) + ax) inside = !inside;
  }
  return inside;
}

function footprintIntersectsCircle(footprint: Row, x: number, y: number, radius: number): boolean {
  const polygons = Array.isArray(footprint.polygons) ? footprint.polygons : [];
  for (const value of polygons) {
    if (!Array.isArray(value) || value.length < 3) continue;
    if (pointInPolygon(x, y, value)) return true;
    for (let i = 0; i < value.length; i++) {
      const a = Array.isArray(value[i]) ? value[i] as unknown[] : [];
      const b = Array.isArray(value[(i + 1) % value.length]) ? value[(i + 1) % value.length] as unknown[] : [];
      const ax = Number(a[0] ?? 0), ay = Number(a[1] ?? 0);
      const dx = Number(b[0] ?? 0) - ax, dy = Number(b[1] ?? 0) - ay;
      const lengthSq = dx * dx + dy * dy;
      const t = lengthSq > 1e-12 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / lengthSq)) : 0;
      if (Math.hypot(x - (ax + dx * t), y - (ay + dy * t)) <= radius + 1e-6) return true;
    }
  }
  return false;
}

/** Circle/height-band twin of ft3d_step_is_difficult_terrain(). */
export function stepIsDifficultTerrain(
  x: number, y: number, z: number, radius: number, footprints: readonly unknown[],
  climate?: DifficultTerrainClimateContext,
): boolean {
  if (climate?.ignoresTerrain) return false;
  for (const { zone, ground } of climate?.zones ?? []) {
    if (ground !== 'none' && !groundConditionIsDifficult(ground, climate!)) continue;
    // Ground contact uses the mover's footprint at its feet, not a retired x/elevation side view or an inflated Z band.
    if (zone.dynamic && zone.holderId.toUpperCase() === climate?.movingZoneHolderId) continue;
    try {
      if (zoneContains(zone, { kind: 'circle', x, y, r: radius, zMin: z, zMax: z })) return true;
    } catch (error) { if (!(error instanceof ZoneHandBack)) throw error; }
  }
  if (climate && !climate.ignoresSnowAndIce) {
    const ground = movementClimateGround(climate);
    if (ground.active && groundConditionIsDifficult(climateGroundAt(ground, x, y, z), climate)) return true;
  }
  for (const value of footprints) {
    let footprint = record(value);
    if (!footprint) continue;
    if (!Array.isArray(footprint.polygons) && footprint.left !== undefined && footprint.right !== undefined) {
      footprint = footprintsFromBoxes([footprint])[0] ?? null;
    }
    if (!footprint || !footprintIntersectsCircle(footprint, x, y, radius)) continue;
    const bottom = Number(footprint.bottom ?? z), top = Number(footprint.top ?? z);
    if (z >= bottom - radius - 1e-6 && z <= top + radius + 1e-6) return true;
  }
  return false;
}

export function encounterDifficultFootprints(encounter: Row): unknown[] {
  if (Array.isArray(encounter.difficult_terrain_footprints) && encounter.difficult_terrain_footprints.length > 0) {
    return encounter.difficult_terrain_footprints;
  }
  return footprintsFromBoxes(Array.isArray(encounter.difficult_terrain_boxes) ? encounter.difficult_terrain_boxes : []);
}

function entryId(entry: unknown, embedded: Row | null): string {
  const wrapper = record(entry);
  const effect = typeof wrapper?.item_layer_effect === 'string' ? wrapper.item_layer_effect.split('|')[0] : undefined;
  return String(wrapper?.status_item_id ?? wrapper?.ability_id ?? effect ?? wrapper?.item_id
    ?? (embedded && bucket(embedded, 'items').item_id) ?? (typeof entry === 'string' ? entry : ''));
}

function resolvedEntry(entry: unknown, embeddedKey: 'item_arr' | 'status_arr', read: DifficultTerrainReaders): Row | null {
  const wrapper = record(entry);
  const embedded = wrapper && record(wrapper[embeddedKey]);
  if (wrapper && record(wrapper.item_layer_types)) return wrapper;
  if (embedded && record(embedded.item_layer_types)) return embedded;
  return read.definition(entryId(entry, embedded));
}

function isAura(definition: Row, read: DifficultTerrainReaders): boolean {
  return ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target']
    .every(name => read.value(definition, name) !== undefined && read.value(definition, name) !== null);
}

/** ft_passive_is_combat_start_timed() (heal_target_arr.php, layer_has twice): triggers_after_combat_begins + a duration_type — the passive
 *  reaches its holder only as the status row its combat-start trigger writes for the window (2026-10-07, Thief L40 "Clean Getaway"). */
function isCombatStartTimed(definition: Row, read: DifficultTerrainReaders): boolean {
  return ['triggers_after_combat_begins', 'duration_type']
    .every(name => read.value(definition, name) !== undefined && read.value(definition, name) !== null);
}

/** Movement exemption twin: root, active non-aura passives, then applied statuses. */
export function moverIgnoresDifficultTerrain(row: Row, read: DifficultTerrainReaders): boolean {
  return moverHasMovementFlag(row, read, 'ignores_difficult_terrain');
}

/** ft_mover_has_movement_flag() twin, any checkbox movement flag (MV1 G0: `burrows_worked_stone` rides it too): the root,
 *  active non-aura, non-combat-start-timed passives, then applied statuses (a timed passive's window status lands there). */
export function moverHasMovementFlag(row: Row, read: DifficultTerrainReaders, layer: string): boolean {
  if (nonEmpty(read.value(row, layer))) return true;
  for (const entry of read.entries(row, 'passive_ability')) {
    const wrapper = record(entry);
    if (wrapper?.passive_conditions_active === false) continue;
    const definition = resolvedEntry(entry, 'item_arr', read);
    if (definition && nonEmpty(read.value(definition, layer)) && !isAura(definition, read) && !isCombatStartTimed(definition, read)) return true;
  }
  for (const status of read.statuses(row)) {
    const definition = resolvedEntry(status, 'status_arr', read);
    if (definition && nonEmpty(read.value(definition, layer))) return true;
  }
  return false;
}

/** squeeze_size_category_offset (2026-10-07, R-170) — any INTEGER movement layer: the LARGEST value among the root, the active non-aura,
 *  non-combat-start-timed passives and the applied statuses (moverHasMovementFlag's walk; ft_mover_movement_int() is the PHP copy). 0 = none. */
export function moverMovementInt(row: Row, read: DifficultTerrainReaders, layer: string): number {
  let best = 0;
  const take = (value: unknown): void => { const n = Math.trunc(Number(scalar(value))); if (Number.isFinite(n) && n > best) best = n; };
  take(read.value(row, layer));
  for (const entry of read.entries(row, 'passive_ability')) {
    const wrapper = record(entry);
    if (wrapper?.passive_conditions_active === false) continue;
    const definition = resolvedEntry(entry, 'item_arr', read);
    if (definition && !isAura(definition, read) && !isCombatStartTimed(definition, read)) take(read.value(definition, layer));
  }
  for (const status of read.statuses(row)) {
    const definition = resolvedEntry(status, 'status_arr', read);
    if (definition) take(read.value(definition, layer));
  }
  return best;
}

/** Does the row carry this catalog item as an active passive or an applied status (Earth Glide for the burrow tier)? */
export function moverCarriesItem(row: Row, read: DifficultTerrainReaders, itemId: string): boolean {
  const wanted = itemId.toUpperCase();
  for (const entry of read.entries(row, 'passive_ability')) {
    const wrapper = record(entry);
    if (wrapper?.passive_conditions_active === false) continue;
    if (entryId(entry, wrapper && record(wrapper.item_arr)).toUpperCase() === wanted) return true;
  }
  return read.statuses(row).some(status => entryId(status, record(status.status_arr)).toUpperCase() === wanted);
}
